import {
  faBusinessTime,
  faCalendarCheck,
  faCalendarPlus,
  faLink,
  faLinkSlash,
  faPlay,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Alert from 'src/components/ui/Alert';
import Button from 'src/components/ui/Button';
import ButtonRow from 'src/components/ui/ButtonRow';
import Input from 'src/components/ui/Input';
import ResourceTitleCard from 'src/components/ui/ResourceTitleCard';
import SectionLabel from 'src/components/ui/SectionLabel';
import SelectableCard from 'src/components/ui/SelectableCard';
import StatusBadge from 'src/components/ui/StatusBadge';
import { DOWNTIME_CHOICE } from 'src/constants/maintenance';
import {
  buildDefaultDowntimeForm,
  serializeDowntimeForm,
  validateDowntimeForm,
} from 'src/utils/maintenance';
import { pluralWord } from 'src/utils/stringUtils';

import { DowntimePickRow, DowntimeViewCard } from './DowntimeCards';

const Field = ({ label, children }) => (
  <div className='mnt-field'>
    <span className='mnt-field__label'>{label}</span>
    {children}
  </div>
);

Field.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node.isRequired };

/**
 * Start screen — confirm start (planned → in_progress). For downtime-capable
 * targets with no linked window, offer None / Create / Use existing. For a
 * planned event that already has a linked window, show it with Change / Unlink.
 */
const StartEvent = ({
  title,
  selectedCount,
  canLinkDowntime,
  linkedDowntime,
  linkableDowntimes,
  isStarting,
  onBack,
  onStartWork,
  onChangeDowntime,
  onUnlinkDowntime,
}) => {
  const [choice, setChoice] = useState(DOWNTIME_CHOICE.NONE);
  const [form, setForm] = useState(() => buildDefaultDowntimeForm(`${title} - maintenance`));
  const [selectedExistingUri, setSelectedExistingUri] = useState(null);

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const downtimeError = choice === DOWNTIME_CHOICE.CREATE ? validateDowntimeForm(form) : null;

  // A future-starting downtime means the operator is scheduling, not working now:
  // the event is saved as Planned (resume it when the window arrives)
  const resolveChosenStart = () => {
    if (!canLinkDowntime || linkedDowntime) return null;
    if (choice === DOWNTIME_CHOICE.CREATE && !downtimeError) {
      return serializeDowntimeForm(form).start;
    }
    if (choice === DOWNTIME_CHOICE.EXISTING && selectedExistingUri) {
      const chosen = linkableDowntimes.find(downtime => downtime.uri === selectedExistingUri);
      return chosen ? chosen.start : null;
    }
    return null;
  };
  const chosenStart = resolveChosenStart();
  const planForLater = Boolean(chosenStart && new Date(chosenStart).getTime() > Date.now());

  const handleStart = () => {
    if (linkedDowntime || !canLinkDowntime) {
      onStartWork(null);
      return;
    }
    if (choice === DOWNTIME_CHOICE.CREATE) {
      if (downtimeError) return;
      onStartWork({ create: serializeDowntimeForm(form) }, planForLater);
      return;
    }
    if (choice === DOWNTIME_CHOICE.EXISTING && selectedExistingUri) {
      onStartWork({ existingUri: selectedExistingUri }, planForLater);
      return;
    }
    onStartWork(null);
  };

  const startDisabled =
    isStarting ||
    Boolean(downtimeError) ||
    (choice === DOWNTIME_CHOICE.EXISTING && !linkedDowntime && !selectedExistingUri);

  return (
    <div>
      <ResourceTitleCard
        icon={faPlay}
        iconColor='linear-gradient(135deg,#16a34a,#15803d)'
        monoSubtitle={false}
        title='Ready to start'
        subtitle={`${selectedCount} ${pluralWord('requirement', selectedCount)} selected`}
      />

      {canLinkDowntime && linkedDowntime && (
        <>
          <SectionLabel icon={faLink}>Linked downtime</SectionLabel>
          <p className='text-muted' style={{ fontSize: 12 }}>
            A downtime window is booked for this event. You can change or unlink it while it’s still
            planned.
          </p>
          <DowntimeViewCard
            downtime={linkedDowntime}
            chip={
              <StatusBadge size='sm' uppercase={false} variant='warning' icon={faLink}>
                Linked
              </StatusBadge>
            }
            actions={
              <ButtonRow style={{ width: '100%' }}>
                <Button
                  block
                  variant='primary'
                  size='small'
                  icon={<FontAwesomeIcon icon={faLink} />}
                  onClick={onChangeDowntime}
                >
                  Change
                </Button>
                <Button
                  block
                  variant='danger'
                  size='small'
                  icon={<FontAwesomeIcon icon={faLinkSlash} />}
                  onClick={onUnlinkDowntime}
                >
                  Unlink
                </Button>
              </ButtonRow>
            }
          />
        </>
      )}

      {canLinkDowntime && !linkedDowntime && (
        <>
          <SectionLabel plain icon={faBusinessTime}>
            Block the machine?
            <StatusBadge size='sm' uppercase={false}>
              Optional
            </StatusBadge>
          </SectionLabel>
          <p className='mnt-section-help'>
            Linking a downtime tells the scheduler this machine is unavailable while you work.
          </p>

          <SelectableCard
            name='mnt-downtime'
            id={DOWNTIME_CHOICE.NONE}
            icon={faLinkSlash}
            title='No downtime'
            desc='Just run maintenance without blocking the schedule.'
            active={choice === DOWNTIME_CHOICE.NONE}
            onSelect={setChoice}
          />
          <SelectableCard
            name='mnt-downtime'
            id={DOWNTIME_CHOICE.CREATE}
            icon={faCalendarPlus}
            title='Create a downtime window'
            desc='Block this machine so the schedule shows it unavailable.'
            active={choice === DOWNTIME_CHOICE.CREATE}
            onSelect={setChoice}
          >
            <Field label='Description'>
              <Input
                value={form.description}
                onChange={event => setField('description', event.target.value)}
              />
            </Field>
            <Field label='Start'>
              <div className='mnt-field__row'>
                <Input
                  type='date'
                  value={form.startDate}
                  onChange={event => setField('startDate', event.target.value)}
                />
                <Input
                  type='time'
                  value={form.startTime}
                  onChange={event => setField('startTime', event.target.value)}
                />
              </div>
            </Field>
            <Field label='Finish'>
              <div className='mnt-field__row'>
                <Input
                  type='date'
                  value={form.finishDate}
                  onChange={event => setField('finishDate', event.target.value)}
                />
                <Input
                  type='time'
                  value={form.finishTime}
                  onChange={event => setField('finishTime', event.target.value)}
                />
              </div>
            </Field>
            {downtimeError && (
              <Alert variant='danger' className='mt-2 mb-0'>
                {downtimeError}
              </Alert>
            )}
          </SelectableCard>
          <SelectableCard
            name='mnt-downtime'
            id={DOWNTIME_CHOICE.EXISTING}
            icon={faCalendarCheck}
            title='Use a scheduled downtime'
            desc='Attach an existing unlinked window on this machine.'
            active={choice === DOWNTIME_CHOICE.EXISTING}
            onSelect={setChoice}
          >
            {linkableDowntimes.length === 0 ? (
              <p className='text-muted mb-0' style={{ fontSize: 12.5 }}>
                No unlinked downtime windows on this machine.
              </p>
            ) : (
              linkableDowntimes.map(downtime => (
                <DowntimePickRow
                  key={downtime.uri}
                  downtime={downtime}
                  selected={selectedExistingUri === downtime.uri}
                  onSelect={picked => setSelectedExistingUri(picked.uri)}
                />
              ))
            )}
          </SelectableCard>
        </>
      )}

      {planForLater && (
        <p className='mnt-section-help mt-2'>
          This window starts later, so the event is saved as Planned. Resume it from the list when
          the window arrives.
        </p>
      )}
      <div className='mnt-footbar'>
        <Button block variant='default' disabled={isStarting} onClick={onBack}>
          Back
        </Button>
        <Button
          fill
          block
          variant='success'
          disabled={startDisabled}
          loading={isStarting}
          icon={<FontAwesomeIcon icon={planForLater ? faCalendarCheck : faPlay} />}
          onClick={handleStart}
        >
          {isStarting
            ? planForLater
              ? 'Planning…'
              : 'Starting…'
            : planForLater
              ? 'Plan for later'
              : 'Start work'}
        </Button>
      </div>
    </div>
  );
};

StartEvent.propTypes = {
  title: PropTypes.string.isRequired,
  selectedCount: PropTypes.number.isRequired,
  canLinkDowntime: PropTypes.bool.isRequired,
  linkedDowntime: PropTypes.object,
  linkableDowntimes: PropTypes.array,
  isStarting: PropTypes.bool,
  onBack: PropTypes.func.isRequired,
  onStartWork: PropTypes.func.isRequired,
  onChangeDowntime: PropTypes.func,
  onUnlinkDowntime: PropTypes.func,
};

StartEvent.defaultProps = {
  linkedDowntime: null,
  linkableDowntimes: [],
  isStarting: false,
  onChangeDowntime: () => {},
  onUnlinkDowntime: () => {},
};

export default StartEvent;
